import React, { useEffect, useMemo, useState } from "react";

function getNodeIcon(node) {
  const nodeType = String(node?.nodeType || "").toLowerCase();
  const category = String(node?.category || node?.definition?.category || "").toLowerCase();
  const name = String(node?.name || "").toLowerCase();

  if (nodeType === "item_assembly_frame") return "🧩";
  if (nodeType === "asset_component") return "🔩";
  if (nodeType === "mounting_item") return "•";
  if (category.includes("hardware") || name.includes("runner")) return "⚙️";
  if (category.includes("board") || name.includes("board") || name.includes("panel")) return "🟫";
  if (name.includes("door")) return "🚪";

  return "⬛";
}

function countChildren(node) {
  return (node.children || []).length;
}

function getNodeTypeLabel(node) {
  const nodeType = String(node?.nodeType || "").toLowerCase();
  const category = String(node?.category || node?.definition?.category || "").toLowerCase();

  if (nodeType === "item_assembly_frame") return "ITEM FRAME";
  if (nodeType === "asset_component") return "COMPONENT";
  if (nodeType === "mounting_item") return "MOUNTING";
  if (category.includes("hardware")) return "HARDWARE";
  if (node?.parentGeneratedFromId || node?.panelKey) return "GENERATED";
  if (node?.definitionId) return `DEF ${node.definitionId}`;

  return "NODE";
}

function getNodeTypeBadgeClass(node) {
  const nodeType = String(node?.nodeType || "").toLowerCase();
  const category = String(node?.category || node?.definition?.category || "").toLowerCase();

  if (nodeType === "item_assembly_frame") return "bg-purple-100 text-purple-700";
  if (nodeType === "asset_component") return "bg-blue-100 text-blue-700";
  if (nodeType === "mounting_item") return "bg-amber-100 text-amber-700";
  if (category.includes("hardware")) return "bg-orange-100 text-orange-700";
  if (node?.parentGeneratedFromId || node?.panelKey) return "bg-slate-100 text-slate-600";

  return "bg-gray-100 text-gray-600";
}

function getOpenTitle(node) {
  const nodeType = String(node?.nodeType || "").toLowerCase();

  if (nodeType === "item_assembly_frame") return "Open item mounting";
  if (nodeType === "asset_component") return "Open item assembly";
  if (nodeType === "mounting_item") return "Open mounting source";
  if (node?.definitionId) return "Open definition";

  return "Open source";
}

function TreeNode({
  node,
  selectedId,
  onSelect,
  onOpen,
  level = 0,
  expanded,
  toggleExpanded,
}) {
  const isSelected = String(selectedId) === String(node.id);
  const childCount = countChildren(node);
  const hasChildren = childCount > 0;
  const isExpanded = expanded.has(String(node.id));

  return (
    <div>
      <div
        className={`group rounded-lg border px-2 py-2 transition ${
          isSelected
            ? "bg-blue-50 border-blue-300 shadow-sm"
            : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
        }`}
        style={{ marginLeft: level * 14 }}
      >
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={() => hasChildren && toggleExpanded(node.id)}
            className={`w-5 h-5 rounded text-xs flex items-center justify-center ${
              hasChildren
                ? "text-gray-700 hover:bg-gray-200"
                : "text-gray-300 cursor-default"
            }`}
          >
            {hasChildren ? (isExpanded ? "▾" : "▸") : "•"}
          </button>

          <button
            type="button"
            onClick={() => onSelect?.(node.id)}
            className="flex-1 text-left min-w-0"
          >
            <div className="flex items-center gap-2 min-w-0">
  <span>{getNodeIcon(node)}</span>

  <span className="font-medium text-sm truncate">
    {node.name || node.id}
  </span>

  <span
    className={[
      "shrink-0 text-[9px] px-1.5 py-0.5 rounded",
      getNodeTypeBadgeClass(node),
    ].join(" ")}
  >
    {getNodeTypeLabel(node)}
  </span>
</div>

<div className="text-[11px] text-gray-500 truncate mt-0.5">
  {node.nodeType ? `type: ${node.nodeType}` : `def: ${node.definitionId ?? "—"}`}
</div>
          </button>

          <button
  type="button"
  onClick={() => onOpen?.(node)}
  className="opacity-0 group-hover:opacity-100 text-xs px-2 py-1 rounded border bg-white"
  title={getOpenTitle(node)}
>
  Open
</button>
        </div>

        {hasChildren ? (
          <div className="mt-1 ml-7 text-[11px] text-gray-400">
            {childCount} child{childCount === 1 ? "" : "ren"}
          </div>
        ) : null}
      </div>

      {hasChildren && isExpanded ? (
        <div className="mt-1 space-y-1">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onOpen={onOpen}
              level={level + 1}
              expanded={expanded}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function collectInitialExpanded(nodes = [], set = new Set()) {
  nodes.forEach((node) => {
    if ((node.children || []).length) {
      set.add(String(node.id));
      collectInitialExpanded(node.children || [], set);
    }
  });
  return set;
}

export default function ModuleChildrenTreePanel({
  nodes = [],
  selectedId,
  onSelect,
  onOpen,
  onAddChild,
}) {
  const initialExpanded = useMemo(() => collectInitialExpanded(nodes), [nodes]);
  const [expanded, setExpanded] = useState(initialExpanded);

  useEffect(() => {
  setExpanded(initialExpanded);
}, [initialExpanded]);

  function toggleExpanded(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      const key = String(id);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }

  function expandAll() {
    setExpanded(collectInitialExpanded(nodes));
  }

  function collapseAll() {
    setExpanded(new Set());
  }

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
  <div>
    <h3 className="font-semibold text-base">Hierarchy</h3>
    <div className="text-xs text-gray-500">
      {nodes.length} root item{nodes.length === 1 ? "" : "s"}
    </div>
  </div>

  <div className="flex gap-1">
    <button
      type="button"
      onClick={onAddChild}
      className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white"
    >
      + Add
    </button>

    <button
      type="button"
      onClick={expandAll}
      className="text-xs px-2 py-1 rounded border bg-white"
    >
      Expand
    </button>

    <button
      type="button"
      onClick={collapseAll}
      className="text-xs px-2 py-1 rounded border bg-white"
    >
      Collapse
    </button>
  </div>
</div>

      {nodes.length ? (
        <div className="space-y-1">
          {nodes.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              selectedId={selectedId}
              onSelect={onSelect}
              onOpen={onOpen}
              expanded={expanded}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-gray-50 text-sm text-gray-500">
          No children yet. Add a board, element, or hardware item.
        </div>
      )}
    </div>
  );
}